import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import InputField from 'packages/novous_web/src/common/components/InputField';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import DateField from 'packages/novous_web/src/common/components/DateField';
import dayjs from 'dayjs';
import TimeField from 'packages/novous_web/src/common/components/TimeField';
import Button from 'packages/novous_web/src/common/components/Button';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

interface ShareLinkModalProps {
    open: boolean;
    onClose: () => void;
    heading?: string;
    shareLink: string;
    subheading?: string;
    vehicleId?: string
}

interface FormData {
    email: string;
    expiryTime: Date | string | null;
    timeStamp: string | null;
}

const ShareLinkModalBox: React.FC<ShareLinkModalProps> = ({
    open,
    onClose,
    heading = "Share Vehicle Details",
    subheading = "Enter an email address to share this Link",
    shareLink,
    vehicleId
}) => {
    const [toast, setToast] = useState({ open: false, message: "", type: "error" });
    const [isSharing, setIsSharing] = useState(false);
    const defaultDateForUI = "DD/MM/YYYY";
    const defaultTimeForUI = "HH:MM:SS";
    const [formData, setFormData] = useState<FormData>({
        email: '',
        expiryTime: defaultDateForUI,
        timeStamp: defaultTimeForUI
    })
    const isToday = formData.expiryTime === defaultDateForUI;
    const [expiryTimeChanged, setExpiryTimeChanged] = useState(false);
    const [timeStampChanged, setTimeStampChanged] = useState(false);
    const [shareLinkGenerated, setShareLinkGenerated] = useState(false);

    const {
        data: shareLinkApiData,
        error: shareLinkApiDataError,
        loading: shareLinkApiDataLoading,
        fetchData: fetchShareLinkApiData,
    } = useAxios<any>(`${API_ENDPOINTS.shareLink.SHARE_LINK}`, "POST");

    const {
        data: shareLinkRunningData,
        error: shareLinkRunningDataError,
        loading: shareLinkRunningDataLoading,
        fetchData: fetchShareLinkRunningData,
    } = useApiError<any>(`${API_ENDPOINTS.shareLink.SHARE_VEHICLE_CURRENT}`, "POST");

    useEffect(() => {
        if (!expiryTimeChanged || !timeStampChanged) return;

        const formattedDate = dayjs(formData.expiryTime, "DD/MM/YYYY").format("YYYY-MM-DD");
        const combinedDateTime = `${formattedDate}:${formData.timeStamp}`;

        fetchShareLinkApiData({
            vehicleId: vehicleId,
            frontendUrl: shareLink,
            expiryTime: combinedDateTime,
        });

        setShareLinkGenerated(true);

    }, [formData.expiryTime, formData.timeStamp]);

    const shareLinkUrl = shareLinkApiData?.data?.frontendUrl

    const handleCloseAndReset = () => {
        setFormData({
            email: '',
            expiryTime: defaultDateForUI,
            timeStamp: defaultTimeForUI
        });
        setExpiryTimeChanged(false);
        setTimeStampChanged(false);
        setShareLinkGenerated(false);
        onClose();
    }

    const handelShare = async () => {
        setIsSharing(true);
        if (!formData?.email || !shareLinkUrl || !expiryTimeChanged || !timeStampChanged) return;
        const formattedDate = dayjs(formData.expiryTime, "DD/MM/YYYY").format("YYYY-MM-DD");
        const combinedDateTime = `${formattedDate}:${formData.timeStamp}`;

        const payload = {
            emailId: formData?.email,
            expiryDateTime: combinedDateTime,
            shareLinkUrl: shareLinkUrl
        }

        try {
            const response = await fetchShareLinkRunningData(payload);
            if (response?.status === 200) {
                setToast({ open: true, message: response?.message, type: "success" });
                handleCloseAndReset();
            } else {
                setToast({ open: true, message: response?.error, type: "error" });
                handleCloseAndReset();
            }
        } catch (error) {
            setToast({ open: true, message: "Something went wrong", type: "error" });
            handleCloseAndReset();
        } finally {
            setIsSharing(false);
        }
    }

    return (
        <>
            <Modal open={open} onClose={() => {
                if (!isSharing) handleCloseAndReset();
            }}>
                <Box
                    sx={{
                        position: 'absolute' as const,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 500 },
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                        }}
                    >
                        <Typography variant="h6">{heading}</Typography>
                        <Typography variant="button" fontWeight={400} className="mb-1" color="secondary">
                            {subheading}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <DateField
                            label="Expiry Time"
                            name='expiryTime'
                            value={formData.expiryTime}
                            onChange={(value) => {
                                setFormData({ ...formData, expiryTime: value });
                                setExpiryTimeChanged(true);
                            }}
                            minDate={dayjs()}
                        />
                        <TimeField
                            label="Time Stamp"
                            name='timeStamp'
                            value={formData.timeStamp}
                            onChange={(value) => {
                                setFormData({ ...formData, timeStamp: value });
                                setTimeStampChanged(true);
                            }}
                            showSeconds={true}
                            isToday={isToday}
                        />
                        {/* {shareLinkGenerated &&
                            <> */}
                                <InputField
                                    label='Share Link'
                                    value={shareLinkUrl}
                                    readOnly
                                    showCopyIcon
                                    onCopyClick={() => {
                                        navigator.clipboard.writeText(shareLinkUrl);
                                    }}
                                    onChange={() => { }}
                                />
                                <InputField
                                    label='Email'
                                    placeholder='Enter Your Email Id'
                                    value={formData.email}
                                    onChange={(e: any) => {
                                        setFormData({ ...formData, email: e.target.value });
                                    }}
                                />
                                <Box
                                    sx={{
                                        marginTop: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 2
                                    }}
                                >
                                    <Button
                                        text='Cancel'
                                        variant="outlined"
                                        onClick={handleCloseAndReset}
                                        disabled={isSharing}
                                    />
                                    <Button
                                        text={isSharing ? 'Sharing Link...' : 'Share Link'}
                                        variant="contained"
                                        onClick={handelShare}
                                        disabled={isSharing || !formData.email.trim()}
                                    />
                                </Box>
                            {/* </>
                        } */}
                    </Box>
                </Box>
            </Modal>

            <AlertToast
                open={toast.open}
                message={toast.message}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                type={toast.type as any}
            />
        </>
    );
}

export default ShareLinkModalBox;